import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, isApiError, queryKeys } from '@/shared/api';

import { type SaveExtra, saveBody } from './model';
import type {
  OccurrenceException,
  OccurrenceOverrides,
  RecurrenceDraft,
  RecurrenceInfo,
  RecurrencePreview,
  RecurrenceRule,
  TemplateEdit,
  RecurrenceBulkDeleteInput,
  RecurrenceBulkDeleteResult,
  RecurrenceBulkPreview,
  RecurrenceBulkPreviewInput,
  RecurrenceBulkEditInput,
  RecurrenceBulkEditPreview,
  RecurrenceBulkEditPreviewInput,
  RecurrenceBulkEditResult,
} from './types';

export function useRecurrence(cardId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.cards.recurrence(cardId),
    queryFn: ({ signal }) => api.get<RecurrenceInfo>(`/v1/cards/${cardId}/recurrence`, { signal }),
    enabled,
  });
}

/** Upcoming individual changes of the series whose source card is `cardId`. */
export function useOccurrenceExceptions(cardId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.cards.recurrenceExceptions(cardId),
    queryFn: ({ signal }) =>
      api.get<OccurrenceException[]>(`/v1/cards/${cardId}/recurrence/occurrences`, { signal }),
    enabled,
  });
}

/** A series change reaches the card dialog, the exception list and every Calendar. */
function refreshSeries(client: QueryClient, cardId: string): Promise<unknown> {
  return Promise.all([
    client.invalidateQueries({ queryKey: queryKeys.cards.recurrence(cardId) }),
    client.invalidateQueries({ queryKey: queryKeys.calendar.all }),
  ]);
}

function useSeriesMutation<V, R = unknown>(cardId: string, request: (variables: V) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: () => refreshSeries(client, cardId),
  });
}

/** Create or replace the schedule. The server computes the next occurrence; nothing is generated in advance. */
export function useSaveRecurrence(cardId: string) {
  return useSeriesMutation(
    cardId,
    ({
      draft,
      timezone,
      ...extra
    }: { draft: RecurrenceDraft; timezone: string | null } & SaveExtra) =>
      api.put<RecurrenceInfo>(`/v1/cards/${cardId}/recurrence`, saveBody(draft, timezone, extra)),
  );
}

/** Dry run of a series revision: where the individual changes would go, and what is ambiguous. */
export function usePreviewRecurrence(cardId: string) {
  return useMutation({
    mutationFn: ({
      draft,
      timezone,
      ...extra
    }: { draft: RecurrenceDraft; timezone: string | null } & SaveExtra) =>
      api.post<RecurrencePreview>(
        `/v1/cards/${cardId}/recurrence/preview`,
        saveBody(draft, timezone, extra),
      ),
  });
}

export function usePauseRecurrence(cardId: string) {
  return useSeriesMutation(cardId, (paused: boolean) =>
    api.post<RecurrenceInfo>(`/v1/cards/${cardId}/recurrence/${paused ? 'pause' : 'resume'}`),
  );
}

/** Stops the series; cards it already made stay linked to it. */
export function useDeleteRecurrence(cardId: string) {
  return useSeriesMutation(cardId, () => api.delete(`/v1/cards/${cardId}/recurrence`));
}

export function usePreviewBulkRecurrenceEdit(sourceCardId: string) {
  return useMutation({
    mutationFn: (body: RecurrenceBulkEditPreviewInput) =>
      api.post<RecurrenceBulkEditPreview>(
        `/v1/cards/${sourceCardId}/recurrence/bulk-edit-preview`,
        body,
      ),
  });
}

export function useApplyBulkRecurrenceEdit(sourceCardId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (body: RecurrenceBulkEditInput) =>
      api.post<RecurrenceBulkEditResult>(`/v1/cards/${sourceCardId}/recurrence/bulk-edit`, body),

    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.cards.all }),
        client.invalidateQueries({ queryKey: queryKeys.boards.all }),
        client.invalidateQueries({ queryKey: queryKeys.calendar.all }),
        client.invalidateQueries({ queryKey: queryKeys.myTasks.all }),
      ]),
  });
}

/** Read-only preview before removing future occurrences and generated cards. */
export function usePreviewBulkRecurrence(sourceCardId: string) {
  return useMutation({
    mutationFn: (body: RecurrenceBulkPreviewInput) =>
      api.post<RecurrenceBulkPreview>(`/v1/cards/${sourceCardId}/recurrence/bulk-preview`, body),
  });
}

/**
 * Delete a confirmed portion of a series.
 * The server rechecks the version, affected card count and permissions.
 */
export function useDeleteBulkRecurrence(sourceCardId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (body: RecurrenceBulkDeleteInput) =>
      api.post<RecurrenceBulkDeleteResult>(
        `/v1/cards/${sourceCardId}/recurrence/bulk-delete`,
        body,
      ),

    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.cards.all }),
        client.invalidateQueries({ queryKey: queryKeys.boards.all }),
        client.invalidateQueries({ queryKey: queryKeys.calendar.all }),
        client.invalidateQueries({ queryKey: queryKeys.myTasks.all }),
      ]),
  });
}

/** Change or cancel ONE upcoming occurrence (no card is created). */
/** The result of "this and following occurrences" (`POST /cards/{id}/recurrence/split`). */
export interface SplitResult {
  /** `following`: the series was split; `series`: the occurrence was the first one, so the whole series changed. */
  mode: 'following' | 'series';
  /** The real card that now sources the series covering the chosen occurrence. */
  source_card_id: string;
  rule: RecurrenceRule;
  previous_rule: RecurrenceRule | null;
  moved_exceptions: number;
  replaced_exception: boolean;
}

/** "This and following": the template edited from one upcoming occurrence on (earlier ones stay). */
export function useSplitSeries(sourceCardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      originalDate: string;
      template: TemplateEdit;
      expectedVersion?: number;
    }) =>
      api.post<SplitResult>(`/v1/cards/${sourceCardId}/recurrence/split`, {
        original_date: body.originalDate,
        template: body.template,
        ...(body.expectedVersion !== undefined && { expected_version: body.expectedVersion }),
      }),
    onSuccess: (result) =>
      Promise.all([
        refreshSeries(client, sourceCardId),
        refreshSeries(client, result.source_card_id),
        client.invalidateQueries({ queryKey: queryKeys.boards.all }),
      ]),
  });
}

export function usePutOccurrence(cardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      originalDate,
      cancelled = false,
      overrides = null,
    }: {
      originalDate: string;
      cancelled?: boolean;
      overrides?: OccurrenceOverrides | null;
    }) =>
      api.put<OccurrenceException | null>(
        `/v1/cards/${cardId}/recurrence/occurrences/${originalDate}`,
        { cancelled, overrides },
      ),
    onSuccess: () =>
      Promise.all([
        refreshSeries(client, cardId),
        client.invalidateQueries({ queryKey: queryKeys.cards.recurrenceExceptions(cardId) }),
      ]),
  });
}

/** Back to the series defaults for one occurrence (also un-cancels it). */
export function useResetOccurrence(cardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (originalDate: string) =>
      api.delete(`/v1/cards/${cardId}/recurrence/occurrences/${originalDate}`),
    onSuccess: () =>
      Promise.all([
        refreshSeries(client, cardId),
        client.invalidateQueries({ queryKey: queryKeys.cards.recurrenceExceptions(cardId) }),
      ]),
  });
}

/** The server asked what to do with the series' individual changes (HTTP 409). */
export function needsExceptionPolicy(error: unknown): number | null {
  if (!isApiError(error) || error.code !== 'RECURRENCE_EXCEPTION_POLICY_REQUIRED') return null;
  const count = error.details?.['exception_count'];
  return typeof count === 'number' ? count : 0;
}

/** The server refused "keep individual changes" as ambiguous: which ones, and why. */
export function exceptionConflicts(error: unknown): { original_date: string; reason: string }[] {
  if (!isApiError(error) || error.code !== 'RECURRENCE_EXCEPTION_CONFLICT') return [];
  const list = error.details?.['conflicts'];
  return Array.isArray(list) ? (list as { original_date: string; reason: string }[]) : [];
}
